/**
 * Native platform bridge — only activates when running inside the Capacitor
 * shell (iOS / Android app). On the web, all exports become no-ops so the
 * same React code runs unchanged in both environments.
 */
import { Capacitor } from "@capacitor/core";
import { api, SITE_ORIGIN } from "./api";

export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform(); // "ios" | "android" | "web"

/**
 * Configure the Android soft keyboard so it doesn't sit on top of input
 * fields when the user taps a chat textarea while the camera/LiveKit view
 * is full-screen. Without this, the WebView keeps its full height when
 * the keyboard appears and the keyboard ends up covering the chat input
 * entirely — which on a Capacitor APK shows up as "no keyboard at all"
 * because nothing visible reacts to the focus.
 *
 * Strategy: ask Capacitor for `KeyboardResize.Native` so the native
 * decor view shifts when the keyboard opens, then keep the accessory
 * bar visible so the user sees the keyboard. Safe to call on web.
 */
export async function configureKeyboard() {
  if (!isNative()) return;
  try {
    const mod = await import("@capacitor/keyboard");
    const Keyboard = mod.Keyboard;
    const KeyboardResize = mod.KeyboardResize;
    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize?.Native || "native" });
    } catch (_) {}
    try {
      await Keyboard.setScroll({ isDisabled: false });
    } catch (_) {}
    // When the keyboard opens, give the focused element a beat then scroll
    // it into view. Solves the case where the chat textarea is bottom-
    // anchored with `bottom: calc(... + safe-area-inset-bottom)` — without
    // a scroll nudge the keyboard occludes it on Samsung / older Android.
    try {
      Keyboard.addListener("keyboardWillShow", () => {
        if (typeof document === "undefined") return;
        const el = document.activeElement;
        if (el && typeof el.scrollIntoView === "function") {
          setTimeout(() => {
            try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {}
          }, 120);
        }
      });
    } catch (_) {}
  } catch (_) {}
}

/**
 * Configure the OS status bar so it overlays the WebView translucently.
 *
 * Android 15+ (targetSdk 35) forces edge-to-edge rendering — without this,
 * the OS status bar/gesture nav crash into the navbar/footer. We:
 *   1. Make the bar background transparent so the page colour shows through.
 *   2. Force dark icons (battery, time, signal) so they read on our white nav.
 *   3. Ask the WebView to render under the bar — our `.safe-top` / `.safe-bottom`
 *      CSS utilities (driven by env(safe-area-inset-*)) push UI content
 *      back below the OS chrome.
 *
 * Safe to call on web (no-ops).
 */
export async function configureStatusBar() {
  if (!isNative()) return;
  // Skip stream pages entirely — RouteAwareChromeShell owns the status
  // bar configuration there (it pins a solid black bar with white icons
  // and uses `overlay: false` so the WebView never bleeds under the OS
  // chrome). Letting this function run there would set `overlay: true`
  // and undo that, hiding the time/battery behind the video.
  const _path = (typeof window !== "undefined" && window.location?.pathname) || "/";
  if (/^\/stream\//.test(_path)) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    try { await StatusBar.show(); } catch (_) {}
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Decide icon appearance based on the current route:
    //   • Live show grid (/live) has a DARK backdrop → light icons.
    //   • Everything else (login, dashboard, browse, etc.) has a
    //     LIGHT background → dark icons. Without this branching, white
    //     icons disappear on the white login page (the bug Noel hit).
    const wantsDarkContent = /^\/live(\b|\/)/.test(_path);
    await StatusBar.setStyle({ style: wantsDarkContent ? Style.Dark : Style.Light });
    if (nativePlatform() === "android") {
      try {
        await StatusBar.setBackgroundColor({
          color: wantsDarkContent ? "#1f1f1f" : "#ffffff",
        });
      } catch (_) {}
    }
    // Re-assert .show() + restyle on every visibility change AND every
    // SPA route change so the bar adapts when the user moves between
    // dark stream pages and light app pages without an app reload.
    if (typeof document !== "undefined") {
      const reShow = () => {
        if (document.visibilityState === "visible") {
          configureStatusBar().catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", reShow);
    }
  } catch (_) {}
}

/**
 * Register this device for push notifications, request permission, send the
 * FCM/APNs token to our backend. Call once after a user logs in.
 *
 * Silently skips when running on the web.
 */
export async function registerPushNotifications(getAuthToken) {
  if (!isNative()) return { ok: false, reason: "not_native" };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === "prompt" || permStatus.receive === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== "granted") return { ok: false, reason: "denied" };
    } else if (permStatus.receive !== "granted") {
      return { ok: false, reason: "denied" };
    }

    await PushNotifications.register();

    return await new Promise((resolve) => {
      const cleanup = () => {
        try {
          PushNotifications.removeAllListeners();
        } catch (_) {}
      };

      PushNotifications.addListener("registration", async (token) => {
        try {
          await api.post(
            "/devices/register",
            { token: token.value, platform: nativePlatform() },
            getAuthToken
              ? { headers: { Authorization: `Bearer ${getAuthToken()}` } }
              : {}
          );
          resolve({ ok: true, token: token.value });
        } catch (e) {
          resolve({ ok: false, reason: "backend_error", error: String(e) });
        }
      });

      PushNotifications.addListener("registrationError", (err) => {
        cleanup();
        resolve({ ok: false, reason: "registration_error", error: err });
      });

      // Tap on a notification — route to the deep link it carries.
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const data = action?.notification?.data || {};
        if (data.deepLink && typeof window !== "undefined") {
          window.location.href = data.deepLink;
        }
      });
    });
  } catch (e) {
    return { ok: false, reason: "exception", error: String(e) };
  }
}

/**
 * Unregister push on logout.
 */
export async function unregisterPushNotifications(token, getAuthToken) {
  if (!isNative() || !token) return;
  try {
    await api.post(
      "/devices/unregister",
      { token },
      getAuthToken ? { headers: { Authorization: `Bearer ${getAuthToken()}` } } : {}
    );
  } catch (_) {
    // best-effort
  }
}

/**
 * Open the native camera on iOS/Android, fall back to standard file input on web.
 * Resolves to a File-like object suitable for FormData upload.
 */
export async function takePhoto() {
  if (!isNative()) {
    // web fallback
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.capture = "environment";
      input.onchange = () => {
        const f = input.files?.[0];
        f ? resolve(f) : reject(new Error("no_file"));
      };
      input.click();
    });
  }

  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    saveToGallery: false,
  });
  // Fetch the native URI and turn it into a File for upload.
  const blob = await fetch(photo.webPath).then((r) => r.blob());
  return new File([blob], `capture-${Date.now()}.${photo.format || "jpg"}`, {
    type: blob.type || "image/jpeg",
  });
}

/**
 * Biometric authentication wrapper. Returns true on success, false on cancel
 * or unavailable.
 */
export async function biometricAuthenticate(reason = "Unlock Bidorah") {
  if (!isNative()) return false;
  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    const { isAvailable } = await NativeBiometric.isAvailable();
    if (!isAvailable) return false;
    await NativeBiometric.verifyIdentity({
      reason,
      title: "Bidorah",
      subtitle: reason,
      description: "Use Face ID / Fingerprint to securely sign in",
    });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Hook: wire Capacitor App URL open events (universal links / app links) into
 * React Router navigation. Call once in App.js inside a useEffect.
 */
export async function initDeepLinks(navigate) {
  if (!isNative()) return;
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appUrlOpen", async (event) => {
      try {
        const raw = event.url;
        // CASE 1: OAuth callback via custom scheme — com.bidorah.app://auth/google?code=...
        // Close the Chrome Custom Tab and route back into the app's
        // auth handler so it can POST the code to /api/auth/google.
        if (raw.startsWith("com.bidorah.app://")) {
          try {
            const { Browser } = await import("@capacitor/browser");
            await Browser.close();
          } catch (_) {}
          // Convert "com.bidorah.app://auth/google?code=..." into
          // "/auth/google?code=..." for React Router. Custom schemes
          // treat the segment after :// as the host, so we have to
          // re-prepend the slash ourselves rather than rely on URL().
          const after = raw.replace(/^com\.bidorah\.app:\/\//, "");
          const path = "/" + after.replace(/^\/+/, "");
          navigate(path);
          return;
        }
        // CASE 2: Universal/app link to bidorah.com — route to the same path.
        const url = new URL(raw);
        const siteOrigin = new URL(SITE_ORIGIN);
        if (url.host === siteOrigin.host || url.host === "bidorah.com") {
          const path = url.pathname + url.search + url.hash;
          navigate(path);
        }
      } catch (_) {}
    });
  } catch (_) {}
}

/**
 * Hide the splash screen once React has mounted — avoids the ugly "flash of
 * splash image" before the web view finishes the first render.
 */
export async function hideSplashWhenReady() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch (_) {}
}
